import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Clock, 
  User, 
  Calendar, 
  CheckCircle, 
  AlertTriangle, 
  Loader2,
  BookOpen,
  XCircle,
  FileText,
  Upload
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TutorHoursEntryPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  
  // Form fields
  const [tutorName, setTutorName] = useState('');
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);
  const [sessionTime, setSessionTime] = useState('');
  const [hoursUsed, setHoursUsed] = useState('');
  const [lessonTopic, setLessonTopic] = useState('');
  const [lessonNotesFile, setLessonNotesFile] = useState(null);
  const [lessonNotesUrl, setLessonNotesUrl] = useState('');
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadStudentInfo();
  }, [token]);

  const loadStudentInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/tutor-link/${token}`);
      setStudentInfo(response.data);
    } catch (err) {
      console.error('Error loading student info:', err);
      if (err.response?.status === 404) {
        setError('Invalid or expired link. Please contact the administrator for a valid link.');
      } else if (err.response?.status === 403) {
        setError('This link has been disabled. Please contact the administrator.');
      } else {
        setError('Failed to load student information. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Invalid file type. Only PDF files are allowed for lesson notes.');
      e.target.value = '';
      return;
    }
    
    // Validate file size (max 20 MB)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 20) {
      toast.error(`File too large (${fileSizeMB.toFixed(1)} MB). Maximum file size is 20 MB.`);
      e.target.value = '';
      return;
    }
    
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/upload/lesson-notes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setLessonNotesFile(file);
      setLessonNotesUrl(response.data.url);
      toast.success('Lesson notes uploaded successfully!');
    } catch (err) {
      console.error('Error uploading PDF:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload lesson notes. Please try again.');
      e.target.value = '';
    } finally {
      setUploadingPdf(false);
    }
  };
  
  const handleRemovePdf = () => {
    setLessonNotesFile(null);
    setLessonNotesUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate tutor name
    if (!tutorName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    // Validate session date
    if (!sessionDate) {
      toast.error('Please select a session date');
      return;
    }
    
    // Validate session time
    if (!sessionTime) {
      toast.error('Please select a session time');
      return;
    }
    
    // Validate hours
    const hours = parseFloat(hoursUsed);
    if (isNaN(hours) || hours <= 0) {
      toast.error('Please enter a valid number of hours greater than 0');
      return;
    }
    
    // Validate lesson topic (required)
    if (!lessonTopic.trim()) {
      toast.error('Please enter the lesson topic');
      return;
    }
    
    // Validate lesson notes PDF (required)
    if (!lessonNotesUrl) {
      toast.error('Lesson notes are required. Please upload one PDF file under 20 MB before submitting hours.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/tutor-link/${token}/log-session`, {
        tutor_name: tutorName.trim(),
        session_date: sessionDate,
        session_time: sessionTime,
        hours_used: hours,
        lesson_topic: lessonTopic.trim(),
        lesson_notes_url: lessonNotesUrl,
        notes: notes.trim()
      });
      
      setSubmitResult(response.data);
      setSubmitted(true);
      toast.success('Hours logged successfully!');
      
      // Update student info with new remaining hours
      setStudentInfo(prev => ({
        ...prev,
        total_hours_remaining: response.data.total_hours_remaining,
        total_hours_used: (prev.total_hours_purchased || 0) - response.data.total_hours_remaining
      }));
      
    } catch (err) {
      console.error('Error submitting hours:', err);
      toast.error(err.response?.data?.detail || 'Failed to log hours. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogAnother = () => {
    setSubmitted(false);
    setSubmitResult(null);
    // Keep tutor name for convenience
    setHoursUsed('');
    setLessonTopic('');
    setLessonNotesFile(null);
    setLessonNotesUrl('');
    setNotes('');
    setSessionDate(new Date().toISOString().split('T')[0]);
    setSessionTime('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Refresh student info
    loadStudentInfo();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted && submitResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-green-200">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Hours Logged!</h2>
            
            <div className="bg-green-50 rounded-xl p-4 my-6 text-left">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Student:</span>
                  <span className="font-semibold text-slate-900">{submitResult.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tutor:</span>
                  <span className="font-semibold text-slate-900">{submitResult.tutor_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Date:</span>
                  <span className="font-semibold text-slate-900">{submitResult.session_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Time:</span>
                  <span className="font-semibold text-slate-900">{submitResult.session_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Hours Logged:</span>
                  <span className="font-semibold text-green-600">{submitResult.hours_logged} hours</span>
                </div>
                {submitResult.lesson_topic && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Lesson Topic:</span>
                    <span className="font-semibold text-slate-900">{submitResult.lesson_topic}</span>
                  </div>
                )}
                {submitResult.lesson_notes_url && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Lesson Notes:</span>
                    <span className="font-semibold text-green-600 flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      Uploaded
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-slate-600">Remaining Hours:</span>
                  <span className="font-bold text-indigo-600">{submitResult.total_hours_remaining} hours</span>
                </div>
              </div>
            </div>

            {submitResult.warning && (
              <Alert className="bg-amber-50 border-amber-200 mb-4 text-left">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  {submitResult.warning}
                </AlertDescription>
              </Alert>
            )}

            <Button 
              onClick={handleLogAnother}
              className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
            >
              Log Another Session
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="h-8 w-8 text-indigo-600" />
          </div>
          <CardTitle className="text-2xl">Log Tutoring Hours</CardTitle>
          <CardDescription>Gifted Academy Tutors</CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Student Info */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{studentInfo?.student_name}</p>
                <p className="text-sm text-slate-500">Student</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-white rounded-lg p-3 border">
                <p className="text-xs text-slate-500 mb-1">Purchased</p>
                <p className="text-lg font-bold text-slate-900">
                  {studentInfo?.total_hours_purchased || 0} hrs
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <p className="text-xs text-slate-500 mb-1">Remaining</p>
                <p className={`text-lg font-bold ${
                  (studentInfo?.total_hours_remaining || 0) <= 2 
                    ? 'text-amber-600' 
                    : 'text-green-600'
                }`}>
                  {studentInfo?.total_hours_remaining || 0} hrs
                </p>
              </div>
            </div>
          </div>

          {/* Warning if low hours */}
          {studentInfo?.total_hours_remaining <= 2 && studentInfo?.total_hours_remaining > 0 && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                Low balance warning: Only {studentInfo.total_hours_remaining} hours remaining.
              </AlertDescription>
            </Alert>
          )}

          {studentInfo?.total_hours_remaining <= 0 && (
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800 text-sm">
                No hours remaining. Please contact admin to add more hours.
              </AlertDescription>
            </Alert>
          )}

          {/* Session Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tutorName" className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                Your Name (Tutor) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="tutorName"
                type="text"
                placeholder="Enter your full name"
                value={tutorName}
                onChange={(e) => setTutorName(e.target.value)}
                required
                data-testid="tutor-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="sessionDate" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="sessionDate"
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="session-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sessionTime" className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="sessionTime"
                  type="time"
                  value={sessionTime}
                  onChange={(e) => setSessionTime(e.target.value)}
                  required
                  data-testid="session-time-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hoursUsed" className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                Hours Used <span className="text-red-500">*</span>
              </Label>
              <Input
                id="hoursUsed"
                type="number"
                step="0.25"
                min="0.25"
                placeholder="e.g., 1.5"
                value={hoursUsed}
                onChange={(e) => setHoursUsed(e.target.value)}
                required
                data-testid="hours-used-input"
              />
              <p className="text-xs text-slate-500">Minimum 0.25 hours (15 minutes)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lessonTopic" className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-slate-500" />
                Lesson Topic <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lessonTopic"
                type="text"
                placeholder="e.g., Quadratic Equations, Essay Writing..."
                value={lessonTopic}
                onChange={(e) => setLessonTopic(e.target.value)}
                required
                data-testid="lesson-topic-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lessonNotes" className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                Lesson Notes (PDF) <span className="text-red-500">*</span>
              </Label>
              
              {!lessonNotesFile ? (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-indigo-300 transition-colors">
                  <input
                    ref={fileInputRef}
                    id="lessonNotes"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePdfUpload}
                    className="hidden"
                    data-testid="lesson-notes-input"
                  />
                  <label 
                    htmlFor="lessonNotes"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    {uploadingPdf ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                        <span className="text-sm text-slate-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          Click to upload PDF (max 20 MB)
                        </span>
                      </>
                    )}
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">{lessonNotesFile.name}</p>
                      <p className="text-xs text-green-600">
                        {(lessonNotesFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemovePdf}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid="remove-pdf-btn"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <p className="text-xs text-slate-500">Upload lesson notes, worksheets, or homework assignments (PDF only)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Progress notes, homework assigned, areas for improvement..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="session-notes-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !tutorName || !sessionDate || !sessionTime || !hoursUsed || !lessonTopic || !lessonNotesUrl}
              className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-hours-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Logging Hours...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Log {hoursUsed || '0'} Hours
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
